import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  CREDIT_KIND_LABEL,
  PAYMENT_STATUS_LABEL,
  canOpenView,
  doctorPath,
  paymentMethodLabel,
} from '@/domain';
import type { StatementCase } from '@/application/ports/FinanceRepository';
import { useDoctorStatement } from '@/application/usecases/useFinance';
import { useSession } from '@/application/usecases/useSession';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { useMoney } from '@/ui/money/moneyView';
import { CaseTable } from '@/ui/data/CaseTable';
import type { Column } from '@/ui/data/ResponsiveTable';
import { StatusDot } from '@/ui/data/StatusDot';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonCase } from '@/ui/feedback/Loading';
import { DoctorIcon, GoBackIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { readFailure, statusOf } from '../clients/clientRefusal';
import { CreditSheet } from './CreditSheet';
import { DoctorPrices } from './DoctorPrices';
import { PAYMENT_STATUS_TONE, signedMoney } from './moneyForm';
import { paymentSummaryPath } from './paymentPaths';
import styles from './Finance.module.css';

const t = strings.finance;

/**
 * One doctor's statement. The request of 8 October 2026 (fourth).
 *
 * Every case of the doctor as a bill: what it owes, what has been paid, what
 * is left, and how long it has been open; over them what they come to
 * together, in four figures; and the doctor's credit with the lab, with every
 * movement of it. Credit is added here and taken off here, and used from a
 * case's own summary. A row opens that case's summary. The cases are the one
 * case table, without its Doctor column: every row would name this doctor.
 *
 * Held by Payments, so whoever takes payments reads it whether or not they
 * hold the Doctors screen; the doctor's profile is linked for whoever does.
 * The name is identity: without it the page is headed by the doctor's ID.
 */
export function DoctorStatementScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const session = useSession().data;
  const views = session?.views ?? [];
  // A doctor's own prices, for whoever may change a price. The lab's request of 17 October 2026, item 11.
  const prices = session?.permissions.includes('pricing') === true;
  const money = useMoney();
  const statement = useDoctorStatement(id);
  const layout = useTableLayout('statement');
  const [credit, setCredit] = useState<'add' | 'remove' | null>(null);

  const back = (
    <Link className={styles.back} to="/payments?list=doctors">
      <GoBackIcon /> {t.backToPayments}
    </Link>
  );

  if (statement.isPending) {
    return (
      <div>
        {back}
        <SkeletonCase label={t.statementLoading} />
      </div>
    );
  }

  if (statement.isError) {
    return (
      <div>
        {back}
        {statusOf(statement.error) === 404 ? (
          <EmptyState icon={<DoctorIcon />} heading={t.doctorNotFound} detail={t.doctorNotFoundDetail} />
        ) : (
          <p className={styles.warn} role="alert">
            {readFailure(statement.error)}
          </p>
        )}
      </div>
    );
  }

  const data = statement.data;
  const name = data.name ?? data.code;

  // Between the job order and patient the case table leads with and the status it ends on.
  const columns: Column<StatementCase>[] = [
    {
      key: 'createdAt',
      label: t.colBooked,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.createdAt,
      render: (row) => formatDay(row.createdAt),
    },
    {
      key: 'due',
      label: t.colDue,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.due,
      render: (row) => money(row.due),
    },
    {
      key: 'paidAmount',
      label: t.colPaid,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.paidAmount,
      render: (row) => money(row.paidAmount),
    },
    {
      key: 'balance',
      label: t.colBalance,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.balance,
      render: (row) => money(row.balance),
    },
    {
      // How long it has been open, which only matters while it still owes something.
      key: 'ageDays',
      label: t.colAge,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => (row.balance > 0 ? row.ageDays : -1),
      render: (row) => (row.balance > 0 ? t.days(row.ageDays) : ''),
    },
  ];

  return (
    <div>
      {back}

      <div className={styles.head}>
        <span className={styles.lbl}>{t.statementLabel}</span>
        <h2 dir="auto">{name}</h2>
        <span className={`${styles.sub} ${styles.mono}`}>{data.code}</span>
        {data.clinic != null && <span className={styles.sub}>{data.clinic}</span>}
      </div>

      <div className={`${styles.figures} ${styles.four}`} role="group" aria-label={t.statementFigures}>
        <div className={styles.figure}>
          <span>{t.billed}</span>
          <b data-testid="statement-billed">{money(data.billed)}</b>
        </div>
        <div className={styles.figure}>
          <span>{t.paid}</span>
          <b data-testid="statement-paid">{money(data.paid)}</b>
        </div>
        <div className={`${styles.figure} ${data.outstanding > 0 ? styles.owed : styles.settled}`}>
          <span>{t.outstanding}</span>
          <b data-testid="statement-outstanding">{money(data.outstanding)}</b>
        </div>
        <div className={styles.figure}>
          <span>{t.credit}</span>
          <b data-testid="statement-credit">{money(data.credit.balance)}</b>
        </div>
      </div>
      {data.cases.some((row) => row.archived) && <p className={styles.hint}>{t.archivedLeftOut}</p>}

      <div className={styles.actions}>
        <Button variant="primary" onClick={() => setCredit('add')}>
          {t.addCredit}
        </Button>
        <Button disabled={data.credit.balance <= 0} onClick={() => setCredit('remove')}>
          {t.removeCredit}
        </Button>
        {canOpenView(views, 'doctors') && (
          <Link className={styles.linkButton} to={doctorPath(data.doctorId)}>
            {t.openProfile}
          </Link>
        )}
      </div>

      <div className={styles.section}>
        <h3>{t.casesHeading}</h3>
      </div>
      <CaseTable
        customize={layout}
        without="doctor"
        columns={columns}
        rows={data.cases}
        identity={(row) => ({ jobOrderNo: row.jobOrderNo, patientName: row.patientName })}
        status={(row) =>
          row.archived ? (
            <StatusDot tone="idle">{t.archivedTag}</StatusDot>
          ) : (
            <StatusDot tone={PAYMENT_STATUS_TONE[row.status]}>{PAYMENT_STATUS_LABEL[row.status]}</StatusDot>
          )
        }
        statusSortValue={(row) => (row.archived ? 'archived' : row.status)}
        rowKey={(row) => row.jobOrderNo}
        onRowClick={(row) => void navigate(paymentSummaryPath(row.jobOrderNo))}
        caption={t.casesCaption(name)}
        emptyHeading={t.noCases}
        emptyDetail={t.noCasesDetail}
      />

      <section className={`${styles.card} ${styles.gap}`} aria-label={t.creditHeading}>
        <div className={styles.section}>
          <h3>{t.creditHeading}</h3>
        </div>
        {data.credit.entries.length === 0 ? (
          <p className={styles.empty}>{t.noCredit}</p>
        ) : (
          <ul className={styles.rows} aria-label={t.creditList}>
            {data.credit.entries.map((entry) => (
              <li key={entry.id} className={styles.row}>
                <div className={styles.what}>
                  <b>
                    {CREDIT_KIND_LABEL[entry.kind]}
                    {entry.jobOrderNo !== null && (
                      <>
                        {' '}
                        <Link className={styles.link} to={paymentSummaryPath(entry.jobOrderNo)}>
                          {t.onCase(entry.jobOrderNo)}
                        </Link>
                      </>
                    )}
                  </b>
                  {entry.note !== null && <span>{entry.note}</span>}
                  <span>
                    {[
                      entry.kind === 'added' && entry.method !== null ? paymentMethodLabel(entry.method) : null,
                      entry.reference,
                      t.by(entry.byName, formatDay(entry.at)),
                    ]
                      .filter((each): each is string => each !== null && each !== '')
                      .join(' · ')}
                  </span>
                </div>
                <div className={`${styles.sum} ${entry.amount < 0 ? '' : styles.minus}`}>{signedMoney(entry.amount, money)}</div>
              </li>
            ))}
          </ul>
        )}
        <p className={`${styles.total} ${styles.grand}`}>
          <span>{t.creditBalance}</span>
          <b>{money(data.credit.balance)}</b>
        </p>
      </section>

      {prices && <DoctorPrices doctorId={data.doctorId} doctorName={name} />}

      {credit !== null && (
        <CreditSheet
          doctorId={data.doctorId}
          doctorName={name}
          balance={data.credit.balance}
          mode={credit}
          onClose={() => setCredit(null)}
        />
      )}
    </div>
  );
}
